<div class="content-card">
  <div class="card-inner">
    <p-fluid>
      <div class="font-semibold text-xl mb-4 module-name">
        Data Preparation
      </div>

      <p-table #dt 
             [value]="viewModel.uploadedFiles" 
             [rows]="10" 
             [paginator]="true"
             [globalFilterFields]="['filename','clientPackageMapping.client.name','clientPackageMapping.package.name','createdBy']"
             [tableStyle]="{ 'min-width': '75rem' }"
             [rowHover]="true"
             dataKey="id" 
             currentPageReportField="Showing {first} to {last} of {totalRecords} files"
             [showCurrentPageReport]="true" 
             [rowsPerPageOptions]="[10, 20, 30]">

        <ng-template #caption>
          <div class="flex flex-col justify-between">
            <div class="flex flex-col items-end gap-3 flex-1 mt-4">
              <div class="flex gap-2">
                <p-button label="Add" 
                          icon="pi pi-plus" 
                          severity="info" 
                          (click)="viewModel.showUploadDialog()" />
              </div>
            </div>
          </div>
          <div class="flex items-center justify-between mt-4">
            <h5 class="m-0"></h5>
            <p-iconfield>
              <p-inputicon styleClass="pi pi-search" />
              <input pInputText type="text" 
                     (input)="viewModel.onGlobalFilter(dt, $event)" 
                     placeholder="Search..." />
            </p-iconfield>
          </div>
        </ng-template>

        <ng-template #header>
          <tr>
            <th style="min-width: 12rem">Client</th>
            <th style="min-width: 12rem">Package</th>
            <th pSortableColumn="filename" style="min-width: 20rem">
              File Name
              <p-sortIcon field="filename" />
            </th>
            <th style="min-width: 12rem">Uploaded By</th>
            <th style="min-width: 14rem">Uploaded On</th>
            <th style="width: 10rem">Action</th>
          </tr>
        </ng-template>

        <ng-template #body let-file>
          <tr>
            <td>{{ file.clientPackageMapping?.client?.name || '-' }}</td>
            <td>{{ file.clientPackageMapping?.package?.packageName || '-' }}</td>
            <td>{{ file.filename }}</td>
            <td>{{ file.lastModifiedByUser ? (file.lastModifiedByUser.firstName + ' ' + file.lastModifiedByUser.lastName) : (file.createdByUser ? (file.createdByUser.firstName + ' ' + file.createdByUser.lastName) : (file.createdBy || '-')) }}</td>
            <td>{{ formatDate(file.lastModifiedOn || file.createdOn) }}</td>
            <td>
              <div class="flex items-center gap-2" style="white-space: nowrap;">
                <p-button icon="pi pi-download" 
                          severity="success" 
                          [rounded]="true" 
                          [outlined]="true" 
                          [raised]="true"
                          (click)="viewModel.downloadFile(file)" 
                          pTooltip="Download" 
                          tooltipPosition="top" />
                <p-button icon="pi pi-pencil" 
                          severity="info" 
                          [rounded]="true" 
                          [outlined]="true" 
                          [raised]="true"
                          (click)="viewModel.editFile(file)" 
                          pTooltip="Edit" 
                          tooltipPosition="top" />
                <p-button icon="pi pi-trash" 
                          severity="danger" 
                          [rounded]="true" 
                          [outlined]="true" 
                          [raised]="true"
                          (click)="viewModel.deleteFile(file)" 
                          pTooltip="Delete" 
                          tooltipPosition="top" />
              </div>
            </td>
          </tr>
        </ng-template>

        <ng-template #emptymessage>
          <tr>
            <td colspan="6" class="noData">No files uploaded yet.</td>
          </tr>
        </ng-template>
      </p-table>
    </p-fluid>

    <!-- Upload Dialog -->
    <p-dialog [(visible)]="viewModel.uploadDialog" 
              [appendTo]="'body'"
              [style]="{width: '500px'}" 
              [header]="viewModel.isEditMode ? 'Edit Data File' : 'Upload Data File'" 
              [modal]="true"
              [draggable]="false"
              [dismissableMask]="false"
              [blockScroll]="true"
              styleClass="module-name data-preparation-dialog"
              (onHide)="viewModel.hideUploadDialog()">
      <div class="p-fluid">
        <!-- Client Dropdown -->
        <div class="field mb-4">
          <label for="client" class="block mb-2">Client <span class="text-red-500">*</span></label>
          <p-select id="client"
                      [options]="viewModel.clients"
                      [(ngModel)]="viewModel.selectedClient"
                      (onChange)="viewModel.onClientChange()"
                      optionLabel="name"
                      placeholder="Select a client"
                      [showClear]="false"
                      class="w-full"></p-select>
        </div>

        <!-- Package Dropdown -->
        <div class="field mb-4">
          <label for="package" class="block mb-2">Package <span class="text-red-500">*</span></label>
          <p-select id="package"
                      [options]="viewModel.packages"
                      [(ngModel)]="viewModel.selectedPackage"
                      optionLabel="package.packageName"
                      placeholder="Select a package"
                      [disabled]="!viewModel.selectedClient"
                      [showClear]="false"
                      class="w-full"></p-select>
        </div>

        <!-- File Upload -->
        <div class="field mb-4">
          <label for="fileUpload" class="block mb-2">Excel File <span *ngIf="!viewModel.isEditMode" class="text-red-500">*</span></label>
          <div class="flex justify-start">
            <p-fileUpload #fileUpload
                          mode="basic"
                          name="file"
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,application/vnd.ms-excel.sheet.macroEnabled.12,.xlsx,.xls,.xlsm"
                          [maxFileSize]="52428800"
                          [auto]="false"
                          chooseLabel="Choose File"
                          (onSelect)="onFileSelect($event)"
                          [disabled]="viewModel.isUploading"></p-fileUpload>
          </div>
          <small class="block mt-1 text-gray-500">{{ viewModel.isEditMode ? 'Leave empty to keep current file. ' : '' }}Supported formats: .xlsx, .xls, .xlsm (Max: 50MB)</small>
          
          <!-- Current File Info (Edit Mode) -->
          <div *ngIf="viewModel.isEditMode && !viewModel.selectedFile && viewModel.currentFile" class="mt-2 p-3 bg-gray-100 rounded">
            <span class="text-sm">
              <i class="pi pi-file-excel mr-2"></i>
              {{ viewModel.currentFile.filename }}
            </span>
          </div>

          <!-- Selected File Info -->
          <div *ngIf="viewModel.selectedFile" class="mt-2 p-3 bg-blue-50 rounded flex justify-between items-center">
            <span class="text-sm">
              <i class="pi pi-file-excel mr-2"></i>
              {{ viewModel.selectedFile.name }} ({{ formatFileSize(viewModel.selectedFile.size) }})
            </span>
            <p-button icon="pi pi-times" 
                      [rounded]="true" 
                      [text]="true" 
                      severity="danger"
                      (click)="clearFile(); fileUpload.clear()" />
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <p-button label="Cancel" 
                  icon="pi pi-times" 
                  severity="warn" 
                  (click)="viewModel.hideUploadDialog()"
                  [disabled]="viewModel.isUploading" />
        <p-button [label]="viewModel.isEditMode ? 'Update' : 'Upload'" 
                  [icon]="viewModel.isEditMode ? 'pi pi-check' : 'pi pi-upload'" 
                  severity="success"
                  (click)="viewModel.uploadFile()"
                  [disabled]="!viewModel.selectedClient || !viewModel.selectedPackage || (!viewModel.selectedFile && !viewModel.isEditMode) || viewModel.isUploading"
                  [pTooltip]="!viewModel.selectedClient ? 'Please select a client' : !viewModel.selectedPackage ? 'Please select a package' : (!viewModel.selectedFile && !viewModel.isEditMode) ? 'Please select a file' : ''"
                  tooltipPosition="top" />
      </ng-template>
    </p-dialog>

    <!-- Confirm Dialog -->
    <p-confirmDialog [style]="{ width: '450px' }"></p-confirmDialog>
  </div>
</div>
